import React from 'react';
import {getAwpApiBase} from '../../../utils/awpApiBase';
import {getAwpClientHeaders} from '../../../session/awpRequestHeaders';
import {
    bustEntityAvatarCacheUrl,
    entityAvatarBaseUrl,
} from '../../../utils/entityAvatarUrl';
import {ENTITY_CARD_SHAPE_TYPE} from '../tldraw/customShapes/entityCardConstants';
import {normalizeEntityCardImageUrl, sanitizeEntityCardPropPatch} from '../tldraw/customShapes/entityCardUtils';

async function fetchEntitySummaryImage(wikidataId) {
    const res = await fetch(
        `${getAwpApiBase()}/api/entities/${encodeURIComponent(wikidataId)}/summary`,
        {headers: getAwpClientHeaders()},
    );
    if (!res.ok) return '';
    const data = await res.json();
    return normalizeEntityCardImageUrl(data?.image || data?.imageUrl || '');
}

/**
 * Refresh entity-card avatar URLs from KB summaries on whiteboard load.
 * Avatar proxy paths are stable after upload, but the browser caches the response for a year.
 */
export function useEntityCardAvatarSync({editorRef, ready, whiteboardId}) {
    const syncedWhiteboardRef = React.useRef(null);

    React.useEffect(() => {
        syncedWhiteboardRef.current = null;
    }, [whiteboardId]);

    React.useEffect(() => {
        if (!ready || !whiteboardId) return undefined;

        const editor = editorRef.current;
        if (!editor || syncedWhiteboardRef.current === whiteboardId) return undefined;

        let cancelled = false;
        let idleHandle = null;
        let timeoutHandle = null;

        const syncAvatars = async () => {
            const shapes = (editor.getCurrentPageShapes?.() || [])
                .filter((shape) => shape?.type === ENTITY_CARD_SHAPE_TYPE && shape.props?.wikidataId);

            const shapesByEntityId = new Map();
            for (const shape of shapes) {
                const entityId = shape.props.wikidataId;
                if (!shapesByEntityId.has(entityId)) {
                    shapesByEntityId.set(entityId, []);
                }
                shapesByEntityId.get(entityId).push(shape);
            }

            if (!shapesByEntityId.size) {
                syncedWhiteboardRef.current = whiteboardId;
                return;
            }

            const updates = [];
            const version = Date.now();

            await Promise.all([...shapesByEntityId.entries()].map(async ([entityId, entityShapes]) => {
                let liveImage = '';
                try {
                    liveImage = await fetchEntitySummaryImage(entityId);
                } catch {
                    return;
                }
                if (cancelled || !liveImage) return;

                const bustedLiveImage = bustEntityAvatarCacheUrl(liveImage, version);
                const liveBase = entityAvatarBaseUrl(bustedLiveImage);

                for (const shape of entityShapes) {
                    const currentImage = shape.props?.imageUrl || '';
                    if (!currentImage || entityAvatarBaseUrl(currentImage) !== liveBase) {
                        updates.push({
                            id: shape.id,
                            type: ENTITY_CARD_SHAPE_TYPE,
                            props: sanitizeEntityCardPropPatch({imageUrl: bustedLiveImage}),
                        });
                        continue;
                    }
                    if (currentImage !== bustedLiveImage) {
                        updates.push({
                            id: shape.id,
                            type: ENTITY_CARD_SHAPE_TYPE,
                            props: sanitizeEntityCardPropPatch({imageUrl: bustedLiveImage}),
                        });
                    }
                }
            }));

            if (cancelled || !updates.length) {
                if (!cancelled) syncedWhiteboardRef.current = whiteboardId;
                return;
            }

            editor.updateShapes?.(updates);
            syncedWhiteboardRef.current = whiteboardId;
        };

        const runSync = () => {
            if (cancelled) return;
            syncAvatars().catch((err) => {
                console.warn('[useEntityCardAvatarSync] Failed to refresh entity card avatars:', err);
                syncedWhiteboardRef.current = whiteboardId;
            });
        };

        if (typeof window.requestIdleCallback === 'function') {
            idleHandle = window.requestIdleCallback(runSync, {timeout: 4000});
        } else {
            timeoutHandle = window.setTimeout(runSync, 250);
        }

        return () => {
            cancelled = true;
            if (idleHandle != null && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleHandle);
            }
            if (timeoutHandle != null) {
                window.clearTimeout(timeoutHandle);
            }
        };
    }, [editorRef, ready, whiteboardId]);
}

/**
 * Build tldraw shape updates for entity-card prop patches.
 * Image updates fan out to every card for the same entity on the canvas.
 *
 * @param {import('tldraw').Editor|null|undefined} editor
 * @param {Record<string, unknown>} partialProps
 * @param {{ wikidataId?: string, shapeId?: string|null }} scope
 * @returns {Array<{ id: string, type: string, props: Record<string, unknown> }>}
 */
export function collectEntityCardShapeUpdates(editor, partialProps, {wikidataId, shapeId} = {}) {
    const props = sanitizeEntityCardPropPatch(partialProps);
    if (!editor || !Object.keys(props).length) return [];

    if (partialProps.imageUrl !== undefined && wikidataId) {
        return (editor.getCurrentPageShapes?.() || [])
            .filter((shape) => (
                shape?.type === ENTITY_CARD_SHAPE_TYPE
                && shape.props?.wikidataId === wikidataId
            ))
            .map((shape) => ({
                id: shape.id,
                type: ENTITY_CARD_SHAPE_TYPE,
                props,
            }));
    }

    if (!shapeId) return [];

    const shape = editor.getShape?.(shapeId);
    if (!shape || shape.type !== ENTITY_CARD_SHAPE_TYPE) return [];

    return [{
        id: shapeId,
        type: ENTITY_CARD_SHAPE_TYPE,
        props,
    }];
}
